// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — the three modes (principle #14). The RULE SET is the axis that changes the
// game, not a dialled number: Meadow is a calm combo-builder on a small grid; Swarm
// spreads more zones and shortens each firefly's life so you must cover more ground
// faster; Sting mixes in wasps you must NOT wave over, turning a hit-everything reflex
// game into a selective one. Grid size ALSO changes how it plays and is MEASURED before
// ship (tests/balance).
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts);
// guests render state().hostOpts. An id off the wire is always modeOf()-validated so an
// unknown value falls back to Meadow rather than reaching the schedule as undefined
// (guarded with Object.hasOwn — a wire key must never resolve a prototype member).

export type ModeId = 'meadow' | 'swarm' | 'sting';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Zone grid: cols across, rows down. zones = cols*rows. */
  readonly cols: number;
  readonly rows: number;
  /** How long a firefly glows before it fades (ms). */
  readonly lifeMs: number;
  /** Gap between one zone's motes, min..max (ms). Controls density/pace. */
  readonly gapMinMs: number;
  readonly gapMaxMs: number;
  /** Initial per-zone stagger so zones don't all bloom in lockstep (ms). */
  readonly leadMs: number;
  /** Fraction of motes that are wasps (avoid them). 0 = only fireflies. */
  readonly waspRate: number;
  /** Round length (ms). */
  readonly limitMs: number;
}

export const MODES: Record<ModeId, Mode> = {
  meadow: {
    id: 'meadow',
    name: 'Meadow',
    blurb: 'A calm six-square meadow. Fireflies linger — catch them in a rhythm and build the combo.',
    cols: 2,
    rows: 3,
    lifeMs: 1500,
    gapMinMs: 800,
    gapMaxMs: 1700,
    leadMs: 1400,
    waspRate: 0,
    limitMs: 60_000,
  },
  swarm: {
    id: 'swarm',
    name: 'Swarm',
    blurb: 'Nine squares, faster fireflies, more at once. Cover the whole grid before they fade.',
    cols: 3,
    rows: 3,
    lifeMs: 1050,
    gapMinMs: 550,
    gapMaxMs: 1200,
    leadMs: 1100,
    waspRate: 0,
    limitMs: 90_000,
  },
  sting: {
    id: 'sting',
    name: 'Sting',
    blurb: 'Wasps hide among the fireflies. Wave over a wasp and you’re stung — so watch where you swing.',
    cols: 2,
    rows: 3,
    lifeMs: 1450,
    gapMinMs: 900,
    gapMaxMs: 1700,
    leadMs: 1400,
    waspRate: 0.32,
    limitMs: 75_000,
  },
};

export const MODE_IDS: ModeId[] = ['meadow', 'swarm', 'sting'];

export const DEFAULT_MODE: Mode = MODES.meadow;

export function zonesOf(mode: Mode): number {
  return mode.cols * mode.rows;
}

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Meadow. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
